import { createElement, useState } from "react";
import { getSettings } from "../common/settingsProvider";
import { createPdfUrl } from "../common/pdfUrlHelper";
import { downloadFile } from "../common/api/downloadFile";
import { useDotLegalSnackbar } from "@dotlegal/dotlegal-ui-components";
import { Trans } from "react-i18next";
import { useTranslation } from "../localization/useTranslation";
import { useUrlProvider } from "../useUrlProvider";
import { useUserContext } from "../auth/userContextProvider/UserContextProvider.tsx";
import { useDownloadManagementReport } from "@common/components/managementReport/DownloadManagementReport.hooks";

export interface IDownloadAuditReportModel {
    legalEntityAuditId: string;
    questionnaireResponseId: string | null;
    responseExternalId: string | null;
    deadline: Date;
    sender: string;
    legalEntityName: string;
    questionnaireName: string;
    hasRecommendations: boolean;
}

export function useDownloadLegalEntityAuditReport() {
    const { translateDate } = useTranslation();

    const [downloadingPdfIds, setDownloadingPdfIds] = useState<Array<string>>([]);
    const { language } = useUserContext();
    const snackbar = useDotLegalSnackbar();
    const urlProvider = useUrlProvider();
    const { onDownloadManagementReport: downloadManagementReport, isDownloadingManagementReport } = useDownloadManagementReport();

    const onDownloadReportAsPdf = async (model: IDownloadAuditReportModel, withoutScoreAndRecommendations: boolean, showRecommendations: boolean) => {
        // Functional updates, because several downloads can be in flight at once and each finishes on its own schedule
        setDownloadingPdfIds((ids) => [...ids, model.legalEntityAuditId]);
        let url = withoutScoreAndRecommendations
            ? getExternalPdfUrl(model.responseExternalId, model.deadline, model.sender, model.legalEntityName, true, showRecommendations)
            : getReportPdfUrl(model.questionnaireResponseId, model.deadline, model.sender, model.legalEntityName, true, showRecommendations);
        if (!url) {
            stopDownloadingPdf(model.legalEntityAuditId);
            return;
        }
        url = createPdfUrl(url, language);
        const fileName = `${model.questionnaireName}_${model.legalEntityName}_${translateDate(model.deadline)}.pdf`;
        await downloadFile(getSettings().apiBaseUrl + "/pdf/generate?noMargin=true&url=" + url, fileName);
        stopDownloadingPdf(model.legalEntityAuditId);
        snackbar.show(createElement(Trans, { i18nKey: "pdfDownloadComplete", values: { fileName: fileName } }));
    };

    function stopDownloadingPdf(legalEntityAuditId: string) {
        setDownloadingPdfIds((ids) => ids.filter((id) => id !== legalEntityAuditId));
    }

    function isDownloadingPdf(legalEntityAuditId: string) {
        return downloadingPdfIds.includes(legalEntityAuditId);
    }

    const onDownloadManagementReport = async (auditGroupId: string, groupName: string, sender: string) => {
        await downloadManagementReport(urlProvider.getAuditGroupManagementReportUrl(auditGroupId), groupName, sender);
    };

    function getReportPdfUrl(
        questionnaireResponseId: string | null,
        deadline: Date,
        sender: string,
        legalEntityName: string,
        pdf: boolean,
        hasRecommendations: boolean
    ) {
        if (!questionnaireResponseId) return "";
        return urlProvider.getQuestionnaireResponseReport(questionnaireResponseId, deadline, sender, legalEntityName, pdf, hasRecommendations);
    }

    function getExternalPdfUrl(
        responseExternalId: string | null,
        deadline: Date,
        sender: string,
        legalEntityName: string,
        pdf: boolean,
        hasRecommendations: boolean
    ) {
        if (!responseExternalId) return "";
        return urlProvider.getQuestionnaireResponseExternalreport(
            responseExternalId,
            deadline,
            sender,
            legalEntityName,
            undefined,
            pdf,
            hasRecommendations,
            false
        );
    }

    return {
        onDownloadReportAsPdf,
        getReportPdfUrl,
        isDownloadingPdf,
        onDownloadManagementReport,
        isDownloadingManagementReport,
    };
}
